import { type CampoSemanticoProcedimento } from '@framework/api-client/api/backoffice/schemas/campoSemanticoProcedimento.ts';
import { type AnagraficaDTO } from '@framework/api-client/api/inquiries/schemas/anagraficaDTO.ts';
import { EnumModuloDTONecessitaFirma } from '@framework/api-client/api/inquiries/schemas/enumModuloDTONecessitaFirma.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { EngineFillableContextProvider } from '@framework/common-web/engine/EngineFillableContextProvider.tsx';
import { useEngineValidationContext } from '@framework/common-web/engine/engine-validation/useEngineValidationContext.ts';
import { useSections } from '@framework/common-web/frontoffice/module/hooks/useSections.ts';
import { type IFrontOfficeModule } from '@framework/common-web/frontoffice/module/IFrontOfficeModule.ts';
import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { DevToolsModuleBridge } from '#features/apps/frontoffice-web/src/dev-tools/bridges/DevToolsModuleBridge.tsx';
import { type ISubmissionNavigator } from '../../navigation/useSubmissionNavigation.ts';
import { SubmissionLogic } from '../../submission/SubmissionLogic.ts';
import { useDelegationContext } from '../delegation/context/DelegationContext.tsx';
import { ModuleStateMachine } from '../fsm/ModuleStateMachine.ts';
import { getModuleTitle } from '../getModuleTitle.ts';
import { FormIncoherenceAlert } from './form-incoherence-alert/FormIncoherenceAlert.tsx';
import { ModuleFormSource } from './ModuleFormSource.ts';
import { ModuleFormWrapper } from './ModuleFormWrapper.tsx';
import { SectionsFillableForm } from './SectionFillableForm.tsx';
import { ModuleFormSectionsMonitor } from './sections-monitor/ModuleFormSectionsMonitor.tsx';
import { useDeserializedModuleValues } from './useDeserializedModuleValues.ts';
import { useModuleFormValidationSchema } from './useModuleFormValidationSchema.ts';
import { useModuleSemanticTags } from './useModuleSemanticTags.ts';
import {
  type IOnAfterSave,
  type IOnBeforeSave,
  useSubmitModuleForm,
} from './useSubmitModuleForm.ts';
import { useValidationContextParams } from './useValidationContextParams.ts';

export interface IModuleFormProps {
  submissionId: string | null | undefined;
  module: IFrontOfficeModule | null | undefined;
  submission: RichiestaDTO | null | undefined;
  /** Campi semantici della pratica (formato backend). */
  dossierSemanticFields: CampoSemanticoProcedimento[] | null | undefined;
  handleNavigation: ISubmissionNavigator;
  registers: AnagraficaDTO[] | null | undefined;

  children?: React.ReactNode;

  before?: React.ReactNode;
  after?: React.ReactNode;

  /** Funzione chiamata al salvataggio avvenuto con successo in bozza */
  onDraftSuccess?: () => Promise<void>;
  /** Funzione chiamata al salvataggio avvenuto con successo */
  onSaveSuccess?: () => Promise<void>;

  /** Funzioni che gestiscono le operazioni da svolgere prima e dopo il salvataggio del modulo */
  onBeforeSave?: IOnBeforeSave;
  onAfterSave?: IOnAfterSave;

  /** Forza il readonly del form */
  readonly?: boolean;

  /** Indica che la richiesta può essere completata in automatico senza bisogno del completamento manuale dell'utente */
  canAutoComplete?: boolean;

  /** Indica se c'è una richiesta in corso del modulo in lettura */
  isFetching: boolean | undefined;

  /** Indica l'applicazione sorgente (FRONTOFFICE, BACKOFFICE, ADMIN) */
  moduleFormSource: ModuleFormSource;
}

const ON_SUBMIT_NOOP = () => {
  /** Funzione vuota di default */
};

/**
 * Componente padre che gestisce la renderizzazione di tutte le sezioni e del relativo form
 */
export function ModuleForm(props: IModuleFormProps) {
  const {
    module,
    submission,
    submissionId,
    handleNavigation,
    onDraftSuccess,
    onSaveSuccess,
    readonly,
    onAfterSave,
    onBeforeSave,
    canAutoComplete,
    dossierSemanticFields,
    registers,
    moduleFormSource,
  } = props;

  const user = useCurrentUser();
  const { sections, values } = useSections(module);

  const delegationContext = useDelegationContext();

  // Gestiamo la visualizzazione del form in sola lettura.
  // Se la richiesta è in pagamento o è inviata, il modulo è in sola lettura
  const isReadonly = useMemo(() => {
    return (
      readonly ??
      // Contiene già la logica di SubmissionLogic.isSubmissionReadonly
      !ModuleStateMachine.can(
        {
          submission,
          module,
          delegationData: delegationContext?.delegationData,
        },
        '.editable',
        user,
      ).allowed
    );
  }, [readonly, submission, module, delegationContext, user]);

  const semanticTags = useModuleSemanticTags(module);

  const validationContextParams = useValidationContextParams({
    submission,
    dossierSemanticFields,
    delegationContext,
    registers,
    semanticTags,
    isReadonly,
    values,
    module,
  });

  /**
   * Nei form readonly, il context di validazione è quello estratto dai valori (contextSnapshot)
   * altrimenti è quello calcolato dai dati esterni del form (session, titolare, ...).
   */
  const validationContext = useEngineValidationContext(validationContextParams);

  const validationSchema = useModuleFormValidationSchema({
    sections,
    isReadonly,
  });

  const initialValues = useDeserializedModuleValues({
    schema: validationSchema.schema,
    values,
    validationContext,
  });

  const handleSubmit = useSubmitModuleForm({
    module,
    moduleId: module?.moduloRichiestaId,
    submissionId,
    validationSchema: validationSchema.schema,
    validationContext,
    sections,
    onSuccess: onSaveSuccess,
    onBeforeSave,
    onAfterSave,
  });

  const handleSubmitFromKeyboard = useCallback(
    async (values: any, _event?: React.BaseSyntheticEvent, methods?: any) => {
      try {
        await handleSubmit(values, methods);
      } catch (e) {
        // biome-ignore lint/complexity/noUselessCatch: Il messaggio di errore è già gestito dall'hook `handleSubmit`. Qui non facciamo nulla, ma siamo comunque obbligati a rilanciare l'errore in quanto serve a prevenire la navigazione.
        throw e;
      }
    },
    [handleSubmit],
  );
  const moduleTitle = getModuleTitle(module);

  // Recupero al funzione di check sul readonly dei widget. A differenza del flag qui sopra, che
  // è da considerarsi globale, la funzione permette di gestire i widget caso per caso.
  // e.g. mettere in readonly solo i widget che non sono integrabili durante un'integrazione.
  const isWidgetReadonly = useMemo(
    () => SubmissionLogic.getIsWidgetReadonlyFunction(submission),
    [submission],
  );

  if (!module?.sezioni) return null;

  return (
    <EngineFillableContextProvider
      submissionModuleId={module.moduloRichiestaId}
      sections={sections}
      submission={submission}
      isBackoffice={moduleFormSource === ModuleFormSource.BACKOFFICE}
    >
      <FormHandler
        key={module.moduloRichiestaId ?? '<undefined>'}
        id={module.moduloRichiestaId ?? '<undefined>'}
        initialValues={initialValues}
        schema={validationSchema.schema}
        validationContext={validationContext}
        castInitialValues
        mode="all"
        onSubmit={isReadonly ? ON_SUBMIT_NOOP : handleSubmitFromKeyboard}
      >
        <ModuleFormSectionsMonitor sections={sections} />
        <FormIncoherenceAlert module={module} />
        <FormErrorsAlert />

        <ModuleFormWrapper
          currentTitle={moduleTitle}
          submissionModuleId={module.moduloRichiestaId}
          submissionId={submissionId}
          submission={submission}
          validationSchema={validationSchema.schema}
          handleNavigation={handleNavigation}
          validationState={module.statoValidazione}
          isReadonly={isReadonly}
          onDraftSuccess={onDraftSuccess}
          moduleState={module.stato}
          hasSignatures={
            module.necessitaFirma !== EnumModuloDTONecessitaFirma.NO
          }
          beforeModule={props.before}
          canAutoComplete={canAutoComplete}
          onBeforeSave={onBeforeSave}
          onAfterSave={onAfterSave}
          isModuleSigned={module.idFileFirmato != null}
          validationContext={validationContext}
          sections={sections}
          module={module}
        >
          <SectionsFillableForm
            sections={sections}
            readonly={isReadonly}
            isReadonly={isWidgetReadonly}
            moduleValues={values}
            moduleSchema={validationSchema.schema}
            moduleFormSource={moduleFormSource}
          />
          <DevToolsModuleBridge
            sections={sections}
            validationSchema={validationSchema.schema}
            module={module}
            submission={submission}
            campiSemanticiPratica={dossierSemanticFields}
          />
          {props.children}
          {props.after}
        </ModuleFormWrapper>
      </FormHandler>
    </EngineFillableContextProvider>
  );
}
